import React, { useMemo } from 'react';
import './ImportGmailDialog.css';

const parseReasons = (reasonsData) => {
    if (!reasonsData) {
        return [];
    }
    let data = reasonsData;
    if (typeof data === 'string') {
        try {
            // To safely handle replacing single quotes to double quotes if the backend returns python stringified lists
            const sanitizedStr = data.replace(/'/g, '"');
            data = JSON.parse(sanitizedStr);
        } catch (e) {
            // If invalid JSON, just treat the raw string as the array element
            data = [data];
        }
    }

    // If it's an array
    if (Array.isArray(data)) {
        return data.map((item) => {
            if (typeof item === 'object' && item !== null) {
                if (item.error) {
                    return {
                        label: item.filename || item.id || '',
                        value: item.error
                    };
                }
                if (item.reason) {
                    return {
                        label: item.filename || item.id || '',
                        value: item.reason
                    };
                }
                return { value: JSON.stringify(item) };
            }
            const strItem = String(item);
            const colonIdx = strItem.indexOf(':');
            if (colonIdx > 0 && colonIdx < 100) {
                // Attempt to extract prefix (like filename) before colon
                return {
                    label: strItem.slice(0, colonIdx),
                    value: strItem.slice(colonIdx + 1).trim()
                };
            }
            return { value: strItem };
        });
    }

    // If it's a dictionary map (like { filename: "reason" })
    if (typeof data === 'object' && data !== null) {
        return Object.entries(data).map(([key, val]) => ({
            label: key,
            value: String(val)
        }));
    }

    return [{ value: String(data) }];
};

const ImportErrorReasonsDialog = ({ close, title, reasonsData, sections }) => {
    // If sections were provided, render them. Otherwise fallback to reasonsData.
    const renderedSections = useMemo(() => {
        if (sections && sections.length > 0) {
            return sections
                .map((sec) => ({
                    title: sec.title,
                    parsed: parseReasons(sec.data),
                    color: sec.color
                }))
                .filter((sec) => sec.parsed.length > 0);
        } else if (reasonsData) {
            return [
                {
                    title: null,
                    parsed: parseReasons(reasonsData)
                }
            ];
        }
        return [];
    }, [sections, reasonsData]);

    return (
        <div className="modal-frame ig-overlay">
            <div
                className="modal-box ig-modal-box"
                style={{ maxWidth: '600px' }}>
                <div className="ig-header">
                    <h2 className="ig-title">{title || 'Import Details'}</h2>
                </div>
                <div
                    className="ig-content"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {renderedSections.length === 0 ? (
                        <p>No details provided.</p>
                    ) : (
                        renderedSections.map((sec, sIdx) => (
                            <div
                                key={sIdx}
                                style={{
                                    marginBottom:
                                        renderedSections.length > 1
                                            ? '20px'
                                            : '0'
                                }}>
                                {sec.title && (
                                    <h3
                                        style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            marginBottom: '10px',
                                            color: '#333'
                                        }}>
                                        {sec.title}
                                    </h3>
                                )}
                                <ul className="reasons-list">
                                    {sec.parsed.map((reasonObj, i) => (
                                        <li
                                            key={i}
                                            style={{
                                                marginBottom: '12px',
                                                paddingBottom: '12px',
                                                borderBottom:
                                                    '1px solid #f0f0f0',
                                                wordBreak: 'break-word',
                                                color: '#555',
                                                lineHeight: '1.5'
                                            }}>
                                            {reasonObj.label ? (
                                                <>
                                                    <span
                                                        style={{
                                                            fontWeight: 600,
                                                            color: '#333',
                                                            marginRight: '6px'
                                                        }}>
                                                        {reasonObj.label}:
                                                    </span>
                                                    <span
                                                        style={{
                                                            color:
                                                                sec.color ||
                                                                '#555'
                                                        }}>
                                                        {reasonObj.value}
                                                    </span>
                                                </>
                                            ) : (
                                                <span
                                                    style={{
                                                        color:
                                                            sec.color || '#555'
                                                    }}>
                                                    {reasonObj.value}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))
                    )}
                </div>
                <div className="ig-footer">
                    <button
                        className="btn btn-outline ig-done-btn"
                        onClick={close}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportErrorReasonsDialog;
